import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Wrapper from '../../components/Wrapper'
import { telecomunication, short as shortLabels } from '../../translate'
import '../../assets/style/style.css'
import Paper from '@mui/material/Paper'
import { Box, Typography, IconButton, TextField, Button, Alert, Autocomplete } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashCan, faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import Api from '../../Api'
import { telegramConnectStyles } from '../../helpers/style'
import { unreadChannelsSet } from '../../store/actions/users'
import Cookies from 'js-cookie'

// Match TelegramInteg main number styles (green theme)
const mainNumberInputSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#7ebc0a' },
    '&:hover fieldset': { borderColor: '#6aa309' },
    '&.Mui-focused fieldset': { borderColor: '#7ebc0a' },
  },
}
const mainNumberSelectSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#7ebc0a' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6aa309' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7ebc0a' },
}
const mainNumberOutlinedButtonSx = {
  borderColor: '#7ebc0a',
  color: '#7ebc0a',
  '&:hover': { borderColor: '#6aa309', backgroundColor: 'rgba(126, 188, 10, 0.1)' },
}
const mainNumberContainedButtonSx = {
  backgroundColor: '#7ebc0a',
  color: 'white',
  '&:hover': { backgroundColor: '#6aa309' },
  '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
}

export default function TelegramConnectChats() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const whatsappEnabled = useSelector((state) => !!state?.users?.unreadWhatsappEnabled)
  const langIndex = +localStorage.getItem('atsLang') || 1

  const [telegramAccounts, setTelegramAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState([])
  
  // Form state
  const [step, setStep] = useState('phone') // phone | code | password | done
  const [phone, setPhone] = useState('')
  const [groupNumber, setGroupNumber] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [accountId, setAccountId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [info, setInfo] = useState('')
  const [editingAccountId, setEditingAccountId] = useState(null)
  const [editingGroupValue, setEditingGroupValue] = useState('')
  const [updateBusy, setUpdateBusy] = useState(false)
  const pollTimer = useRef(null)

  const fetchTelegramAccounts = useCallback(async () => {
    try {
      if(Cookies.get('viewer')){
        return
      }
      setLoading(true)
      const res = await Api.telegramGet()
      const rows = Array.isArray(res?.data?.data) ? res.data.data : []
      setTelegramAccounts(rows)
      const ids = rows
        .map((x) => Number(x?.id || x?.ID || 0))
        .filter((n) => Number.isFinite(n) && n > 0)
      dispatch(unreadChannelsSet({
        whatsappEnabled,
        telegramEnabled: ids.length > 0,
        telegramMainId: ids.length > 0 ? ids[0] : null,
      }))
    } catch (err) {
      console.error('Failed to get telegram accounts:', err)
      setTelegramAccounts([])
      dispatch(unreadChannelsSet({
        whatsappEnabled,
        telegramEnabled: false,
        telegramMainId: null,
      }))
    } finally {
      setLoading(false)
    }
  }, [dispatch, whatsappEnabled])

  useEffect(() => {
    if(Cookies.get('viewer')){
      return
    }
    fetchTelegramAccounts()
  }, [fetchTelegramAccounts])

  const fetchGroups = async () => {
    try {
      const response = await Api.groupsGet(1)
      if (response?.data?.status === 'ok') {
        const shortGroups = response.data.data || []
        const allGroups = shortGroups.map(g => ({
          id: g.id,
          virtual: g.virtual,
          name: g.name || `Short ${g.virtual}`,
          label: `${g.virtual}${g.name ? ` - ${g.name}` : ''}`,
          isivr: g.isivr ?? 0,
        }))
        setGroups(allGroups)
      }
    } catch (error) {
      console.error('Error fetching groups:', error)
    }
  }

  useEffect(() => {
    fetchGroups()
  }, [])

  const handleStartEdit = useCallback((account) => {
    setEditingAccountId(account.id)
    const groupInfo = groups.find((g) => g.id === account.group_id) ||
      groups.find((g) => String(g.virtual) === String(account.group_id)) ||
      groups.find((g) => String(g.virtual) === String(account.group_virtual))
    setEditingGroupValue(groupInfo?.virtual != null ? String(groupInfo.virtual) : '')
  }, [groups])

  const handleCancelEdit = useCallback(() => {
    setEditingAccountId(null)
    setEditingGroupValue('')
  }, [])

  const handleSaveEdit = useCallback(async () => {
    if (!editingAccountId) return
    setUpdateBusy(true)
    try {
      await Api.telegramUpdate(editingAccountId, editingGroupValue || null)
      await fetchTelegramAccounts()
      setEditingAccountId(null)
      setEditingGroupValue('')
    } catch (e) {
      console.error('Failed to update telegram connection:', e)
      setErr(e?.response?.data?.error || e?.message || 'Update failed')
    } finally {
      setUpdateBusy(false)
    }
  }, [editingAccountId, editingGroupValue, fetchTelegramAccounts])

  const handleDelete = useCallback(async (accountId) => {
    const deleteText = langIndex === 1 ? "Are you sure you want to delete this Telegram connection?" :
      langIndex === 2 ? "Вы уверены, что хотите удалить это подключение Telegram?" :
        "Վստա՞հ եք, որ ցանկանում եք ջնջել այս Telegram միացումը?"
    
    if (window.confirm(deleteText)) {
      try {
        await Api.telegramDelete(accountId)
        await fetchTelegramAccounts()
      } catch (e) {
        console.error('Failed to delete telegram connection:', e)
      }
    }
  }, [langIndex, fetchTelegramAccounts])

  function stopPolling() {
    if (pollTimer.current) {
      clearInterval(pollTimer.current)
      pollTimer.current = null
    }
  }

  function resetForm() {
    stopPolling()
    setStep('phone')
    setPhone('')
    setGroupNumber('')
    setCode('')
    setPassword('')
    setAccountId(null)
    setBusy(false)
    setErr('')
    setInfo('')
  }

  const handleStart = useCallback(async () => {
    const ph = phone.trim()
    const groupId = groupNumber
    if (!ph) {
      setErr(telecomunication.modal_error_enter_phone?.[langIndex] || 'Please enter phone number')
      return
    }
    setBusy(true)
    setErr('')

    try {
      const r = await Api.telegramStart(ph, groupId)
      const data = r?.data || r

      if (!data?.ok) {
        setErr(data?.error || telecomunication.modal_error_start_failed?.[langIndex] || 'Failed to start')
        return
      }

      setAccountId(Number(data.accountId))
      setStep('code')
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || telecomunication.modal_error_start_failed?.[langIndex] || 'Failed to start')
    } finally {
      setBusy(false)
    }
  }, [phone, groupNumber, langIndex])

  const handleSubmitCode = useCallback(async () => {
    const c = code.trim()
    if (!c) {
      setErr(telecomunication.modal_error_enter_code?.[langIndex] || 'Please enter code')
      return
    }
    if (!accountId) {
      setErr(telecomunication.modal_error_missing_account?.[langIndex] || 'Missing account ID')
      return
    }
    setBusy(true)
    setErr('')

    try {
      const r = await Api.telegramCode(c, accountId)
      const data = r?.data || r

      if (!data?.ok) {
        setInfo(telecomunication.try?.[langIndex] || data?.info?.[langIndex] || data?.error?.[langIndex] || '')
        setErr(telecomunication.modal_error_code_failed?.[langIndex] || 'Code verification failed')
        return
      }

      if (data?.needPassword) {
        setStep('password')
      } else {
        setStep('done')
        setTimeout(() => {
          resetForm()
          fetchTelegramAccounts()
        }, 2000)
       
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || telecomunication.modal_error_code_failed?.[langIndex] || 'Code verification failed')
    } finally {
      setBusy(false)
    }
  }, [code, accountId, langIndex, fetchTelegramAccounts])

  const handleSubmitPassword = useCallback(async () => {
    const p = password.trim()
    if (!p) {
      setErr(telecomunication.modal_error_enter_password?.[langIndex] || 'Please enter password')
      return
    }
    if (!accountId) {
      setErr(telecomunication.modal_error_missing_account?.[langIndex] || 'Missing account ID')
      return
    }
    setBusy(true)
    setErr('')

    try {
      const r = await Api.telegramPassword(p, accountId)
      const data = r?.data || r

      if (!data?.ok) {
        setErr(data?.error || telecomunication.modal_error_password_failed?.[langIndex] || 'Password verification failed')
        return
      }

      setStep('done')
      setTimeout(() => {
        resetForm()
        fetchTelegramAccounts()
      }, 2000)
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || telecomunication.modal_error_password_failed?.[langIndex] || 'Password verification failed')
    } finally {
      setBusy(false)
    }
  }, [password, accountId, langIndex, fetchTelegramAccounts])

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, pb: 2, borderBottom: '1px solid #e5e8ed' }}>
            <IconButton
              size="small"
              sx={{ color: '#7ebc0a', fontSize: 18 }}
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {telecomunication.telegram_handle_messaging[langIndex] || 'Telegram Connections'}
            </Typography>
          </Box>

          {/* Add new connection form - styles match TelegramInteg main number */}
          <Paper sx={{ p: 2, border: '1px solid #7ebc0a', mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid #e5e8ed', fontWeight: 700 }}>
              {telecomunication.modal_title?.[langIndex] || 'Add New Telegram Connection'}
            </Typography>

            {step === 'phone' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={telecomunication.modal_phone_label?.[langIndex] || 'Phone Number'}
                  placeholder={telecomunication.modal_phone_placeholder?.[langIndex] || '+1234567890'}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  fullWidth
                  disabled={busy}
                  sx={mainNumberInputSx}
                />
                <Autocomplete
                  fullWidth
                  disabled={busy}
                  options={[
                    { id: null, virtual: '', name: '', label: langIndex === 1 ? 'None' : langIndex === 2 ? 'Нет' : 'Ոչ մեկը', isivr: -1 },
                    ...groups,
                  ]}
                  value={groupNumber === '' ? { id: null, virtual: '', name: '', label: langIndex === 1 ? 'None' : langIndex === 2 ? 'Нет' : 'Ոչ մեկը', isivr: -1 } : (groups.find((g) => String(g.virtual) === String(groupNumber)) || null)}
                  onChange={(_, newValue) => setGroupNumber(newValue?.virtual != null ? String(newValue.virtual) : '')}
                  getOptionLabel={(option) => (option && option.label) || ''}
                  groupBy={(option) => {
                    if (!option || option.virtual === '') return langIndex === 1 ? '— None' : langIndex === 2 ? '— Нет' : '— Ոչ մեկը'
                    if (option.isivr === 1) return shortLabels.ivr_lists?.[langIndex] ?? 'IVR'
                    return shortLabels.group?.[langIndex] ?? 'Group'
                  }}
                  isOptionEqualToValue={(opt, val) => opt && val && String(opt.virtual) === String(val.virtual)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={langIndex === 1 ? 'Group Number (Optional)' : langIndex === 2 ? 'Номер группы (Необязательно)' : 'Խմբի համար (Ընտրովի)'}
                      placeholder={langIndex === 1 ? 'Search or select...' : langIndex === 2 ? 'Поиск или выбор...' : 'Որոնել կամ ընտրել...'}
                      sx={{
                        ...mainNumberInputSx,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#7ebc0a' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6aa309' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7ebc0a' },
                      }}
                      InputLabelProps={{ ...params.InputLabelProps, sx: { ...params.InputLabelProps?.sx, '&.Mui-focused': { color: '#7ebc0a' } } }}
                    />
                  )}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue.trim()) return options
                    const q = inputValue.trim().toLowerCase()
                    return options.filter(
                      (opt) =>
                        opt.virtual === '' ||
                        (opt.label && String(opt.label).toLowerCase().includes(q)) ||
                        (opt.name && String(opt.name).toLowerCase().includes(q)) ||
                        (opt.virtual != null && String(opt.virtual).toLowerCase().includes(q))
                    )
                  }}
                />
                {telecomunication.modal_phone_hint?.[langIndex] && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                    {telecomunication.modal_phone_hint[langIndex]}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={busy}
                    sx={mainNumberOutlinedButtonSx}
                  >
                    {telecomunication.modal_button_cancel?.[langIndex] || 'Cancel'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleStart}
                    disabled={busy || !phone.trim()}
                    sx={mainNumberContainedButtonSx}
                  >
                    {busy ? (telecomunication.modal_button_starting?.[langIndex] || 'Starting...') : (telecomunication.modal_button_send_code?.[langIndex] || 'Send Code')}
                  </Button>
                </Box>
              </Box>
            )}

            {step === 'code' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={telecomunication.modal_code_label?.[langIndex] || 'Verification Code'}
                  placeholder={telecomunication.modal_code_placeholder?.[langIndex] || 'Enter code'}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  fullWidth
                  disabled={busy}
                  onKeyDown={(e) => e.key === 'Enter' && !busy && handleSubmitCode()}
                  sx={mainNumberInputSx}
                />
                {telecomunication.modal_code_hint?.[langIndex] && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                    {telecomunication.modal_code_hint[langIndex]}
                  </Typography>
                )}
                {info && (
                  <Alert severity="info" sx={{ fontSize: 12 }}>
                    {info}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setStep('phone')
                      setAccountId(null)
                      setCode('')
                      setErr('')
                      setInfo('')
                    }}
                    disabled={busy}
                    sx={mainNumberOutlinedButtonSx}
                  >
                    {telecomunication.modal_button_back?.[langIndex] || 'Back'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitCode}
                    disabled={busy || !code.trim()}
                    sx={mainNumberContainedButtonSx}
                  >
                    {busy ? (telecomunication.modal_button_verifying?.[langIndex] || 'Verifying...') : (telecomunication.modal_button_verify_code?.[langIndex] || 'Verify Code')}
                  </Button>
                </Box>
              </Box>
            )}

            {step === 'password' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={telecomunication.modal_password_label?.[langIndex] || 'Password'}
                  placeholder={telecomunication.modal_password_placeholder?.[langIndex] || 'Enter password'}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  disabled={busy}
                  onKeyDown={(e) => e.key === 'Enter' && !busy && handleSubmitPassword()}
                  sx={mainNumberInputSx}
                />
                {telecomunication.modal_password_hint?.[langIndex] && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                    {telecomunication.modal_password_hint[langIndex]}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={busy}
                    sx={mainNumberOutlinedButtonSx}
                  >
                    {telecomunication.modal_button_cancel?.[langIndex] || 'Cancel'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitPassword}
                    disabled={busy || !password.trim()}
                    sx={mainNumberContainedButtonSx}
                  >
                    {busy ? (telecomunication.modal_button_connecting?.[langIndex] || 'Connecting...') : (telecomunication.modal_button_connect?.[langIndex] || 'Connect')}
                  </Button>
                </Box>
              </Box>
            )}

            {step === 'done' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 2 }}>
                <Alert severity="success" sx={{ width: '100%' }}>
                  {telecomunication.modal_done_message?.[langIndex] || 'Connection successful!'}
                </Alert>
                <Button
                  variant="contained"
                  onClick={resetForm}
                  sx={mainNumberContainedButtonSx}
                >
                  {telecomunication.modal_button_close?.[langIndex] || 'Close'}
                </Button>
              </Box>
            )}

            {err && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {err}
              </Alert>
            )}
          </Paper>

          {/* Existing connections - cards */}
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Loading...</Typography>
            </Box>
          ) : telegramAccounts.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, pb: 1.5, borderBottom: '1px solid #e5e8ed' }}>
                {telecomunication.telegram_handle_messaging[langIndex] || 'Telegram Connections'}
              </Typography>
              <div className="short-cards">
                {telegramAccounts.map((account) => {
                  const accountTitle = account.phone
                    ? `${telecomunication.telegram_handle_messaging[langIndex] || 'Telegram'} - ${account.phone}`
                    : `${telecomunication.telegram_handle_messaging[langIndex] || 'Telegram'} #${account.id || ''}`

                  const groupInfo = groups.find((g) => g.id === account.group_id) ||
                    groups.find((g) => String(g.virtual) === String(account.group_id)) ||
                    groups.find((g) => String(g.virtual) === String(account.group_virtual))
                  const groupText = groupInfo
                    ? groupInfo.label
                    : account.group_id
                      ? (langIndex === 1 ? `Group: ${account.group_id}` : langIndex === 2 ? `Группа: ${account.group_id}` : `Խումբ: ${account.group_id}`)
                      : account.group_virtual
                        ? (langIndex === 1 ? `Group: ${account.group_virtual}` : langIndex === 2 ? `Группа: ${account.group_virtual}` : `Խումբ: ${account.group_virtual}`)
                        : ''

                  const descParts = []
                  if (account.tg_user_id) descParts.push(`User ID: ${account.tg_user_id}`)
                  if (account.phone) descParts.push(`Phone: ${account.phone}`)
                  if (groupText) descParts.push(groupText)
                  const fallbackDesc = telecomunication.telegram_handle_messaging_desc[langIndex] || 'Telegram connection'

                  const statusLabel = account.status === 'waiting_for_code' ? telecomunication.waiting_for_code[langIndex] : account.status === 'ready' ? telecomunication.ready[langIndex] : account.status === 'error' ? telecomunication.error[langIndex] : account.status || ''
                  const isClickable = account.status === 'ready'
                  const openPath = `/${window.location.pathname.includes('short_function') ? 'short_function/chats/' : 'connect/'}telegram/${account.id}`

                  const isEditing = editingAccountId === account.id

                  return (
                    <Paper
                      key={account.id}
                      className="short-card"
                      elevation={0}
                      onClick={isEditing ? undefined : (isClickable ? () => navigate(openPath) : undefined)}
                      sx={isClickable && !isEditing ? { cursor: 'pointer' } : {}}
                    >
                      <div className="short-card__label">
                        <Typography component="p" variant="body2" sx={{ margin: 0 }}>
                          {accountTitle}
                        </Typography>
                      </div>
                      <div className="short-card__code">
                        {isEditing ? (
                          <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                            <Autocomplete
                              size="small"
                              options={[
                                { id: null, virtual: '', name: '', label: langIndex === 1 ? 'None' : langIndex === 2 ? 'Нет' : 'Ոչ մեկը', isivr: -1 },
                                ...groups,
                              ]}
                              value={editingGroupValue === '' ? { id: null, virtual: '', name: '', label: langIndex === 1 ? 'None' : langIndex === 2 ? 'Нет' : 'Ոչ մեկը', isivr: -1 } : (groups.find((g) => String(g.virtual) === String(editingGroupValue)) || null)}
                              onChange={(_, newValue) => setEditingGroupValue(newValue?.virtual != null ? String(newValue.virtual) : '')}
                              getOptionLabel={(option) => (option && option.label) || ''}
                              groupBy={(option) => {
                                if (!option || option.virtual === '') return langIndex === 1 ? '— None' : langIndex === 2 ? '— Нет' : '— Ոչ մեկը'
                                if (option.isivr === 1) return shortLabels.ivr_lists?.[langIndex] ?? 'IVR'
                                return shortLabels.group?.[langIndex] ?? 'Group'
                              }}
                              isOptionEqualToValue={(opt, val) => opt && val && String(opt.virtual) === String(val.virtual)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={langIndex === 1 ? 'Group' : langIndex === 2 ? 'Группа' : 'Խումբ'}
                                  sx={{
                                    ...mainNumberInputSx,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#7ebc0a' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6aa309' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7ebc0a' },
                                  }}
                                />
                              )}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                              <Button size="small" variant="outlined" onClick={handleCancelEdit} disabled={updateBusy} sx={mainNumberOutlinedButtonSx}>
                                {langIndex === 1 ? 'Cancel' : langIndex === 2 ? 'Отмена' : 'Չեղարկել'}
                              </Button>
                              <Button size="small" variant="contained" onClick={handleSaveEdit} disabled={updateBusy} sx={mainNumberContainedButtonSx}>
                                {updateBusy ? (langIndex === 1 ? 'Saving...' : langIndex === 2 ? 'Сохранение...' : 'Պահվում է...') : (langIndex === 1 ? 'Save' : langIndex === 2 ? 'Сохранить' : 'Պահել')}
                              </Button>
                            </Box>
                          </Box>
                        ) : descParts.length > 0 ? (
                          descParts.map((part, i) => (
                            <Typography key={i} component="p" variant="body2" sx={{ margin: 0, mb: i < descParts.length - 1 ? 0.5 : 0 }}>
                              {part}
                            </Typography>
                          ))
                        ) : (
                          <Typography component="p" variant="body2" sx={{ margin: 0, mb: 0.5 }}>
                            {fallbackDesc}
                          </Typography>
                        )}
                      </div>
                      <div className="short-card__actions" style={{ alignItems: 'center' }}>
                        {account.status ? (
                          <Typography component="span" variant="body2" sx={telegramConnectStyles.statusBadge(account.status)}>
                            {statusLabel}
                          </Typography>
                        ) : (
                          <span />
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {!isEditing && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation()
                                handleStartEdit(account)
                              }}
                              style={{ color: '#7ebc0a', cursor: 'pointer', fontSize: 16 }}
                              title={langIndex === 1 ? 'Edit' : langIndex === 2 ? 'Редактировать' : 'Խմբագրել'}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </span>
                          )}
                          <span
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(account.id)
                            }}
                            className="delete"
                            style={{ color: '#ce405f', cursor: 'pointer', fontSize: 16 }}
                            title={langIndex === 1 ? 'Delete' : langIndex === 2 ? 'Удалить' : 'Ջնջել'}
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </span>
                        </Box>
                      </div>
                    </Paper>
                  )
                })}
              </div>
            </>
          ) : !loading ? (
              <Box sx={telegramConnectStyles.emptyStateBox}>
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={telegramConnectStyles.emptyStateText}
                >
                  {langIndex === 1 ? 'No Telegram connections yet. Add one to get started.' :
                   langIndex === 2 ? 'Пока нет подключений Telegram. Добавьте одно, чтобы начать.' :
                   'Դեռ Telegram միացումներ չկան: Ավելացրեք սկսելու համար:'}
                </Typography>
              </Box>
            ) : null}
        </Paper>
      </div>
    </Wrapper>
  )
}
